import { resolveGenericStatusCardIcon } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { resolveEntityTags, resolveTagColor } from '@/features/shared/tags';
import { featureStatusOptions } from '@/shared/constants/statusOptions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { Box, EventOutlinedIcon, ReleasesIcon } from '@/ui';
import {
  getSingleSelectOptionLabel,
  getSingleSelectOptionValue,
} from '@/ui/composites/grid/gridColumns';
import {
  getSingleSelectFilterOperators,
  getStringFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { resolveStatusChipTone, type GridColDef } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
import type { FeatureDto } from '@flowqa/shared';

type FeaturesColumnsOptions = {
  onProjectSelect: (projectId: string) => void;
  onDeleteRow?: (row: FeatureDto) => void;
  tagColorByName?: Partial<Record<string, TagColor>>;
  deleteRowPendingId?: string | null;
  onUpdateRowTagsColor?: (input: { row: FeatureDto; tags: string[]; colors: TagColor[] }) => void;
  updateRowTagsColorPendingId?: string | null;
};

const columnFlex = { name: 1, status: 0.9, suites: 1, tests: 1 };

export const buildFeaturesColumns = ({
  onProjectSelect,
  onDeleteRow,
  tagColorByName = {},
  deleteRowPendingId = null,
  onUpdateRowTagsColor,
  updateRowTagsColorPendingId = null,
}: FeaturesColumnsOptions): Array<GridColDef<FeatureDto>> => {
  const stringOperators = getStringFilterOperators();
  const singleSelectOperators = getSingleSelectFilterOperators();
  const nameColumn: GridColDef<FeatureDto> = {
    field: 'name',
    headerName: 'Feature',
    flex: columnFlex.name,
    cellClassName: 'table-name-cell',
    valueGetter: (_value, row) => row.name,
    renderCell: (params) => (
      <TableCellContainer gap="none">
        <TableNameCell
          primary={{
            label: params.row.name,
            href: `/features/${params.row.slug}`,
            onClick: () => {
              onProjectSelect(params.row.projectId);
            },
          }}
          secondary={
            params.row.releaseName
              ? {
                  label: params.row.releaseName,
                  href: params.row.releaseSlug ? `/releases/${params.row.releaseSlug}` : undefined,
                  role: 'caption',
                  sx: { display: 'inline-flex', alignItems: 'center' },
                }
              : null
          }
          tags={resolveEntityTags(params.row).map((tag) => ({
            label: tag,
            tone: resolveTagColor(tag, tagColorByName),
          }))}
        />
      </TableCellContainer>
    ),
  };
  nameColumn.filterOperators = stringOperators;

  const statusColumn: GridColDef<FeatureDto> = {
    field: 'status',
    headerName: getDetailLabel('status'),
    flex: columnFlex.status,
    align: 'left',
    headerAlign: 'left',
    type: 'singleSelect',
    valueOptions: featureStatusOptions,
    getOptionLabel: getSingleSelectOptionLabel,
    getOptionValue: getSingleSelectOptionValue,
    filterOperators: singleSelectOperators,
    renderCell: (params) => {
      const status = params.row.status;
      const statusTone = resolveStatusChipTone(status);
      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={params.formattedValue?.toString() ?? '-'}
            chipTone={statusTone}
            chipIcon={<Box component={resolveGenericStatusCardIcon(status)} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={formatDate(params.row.plannedFor) || ''}
            durationTitle="Planned For"
            durationIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={formatDate(params.row.releasedAt) || ''}
            timestampTitle={formatDate(params.row.releasedAt, { includeTime: true }) || null}
            timestampRowTitle="Released At"
            timestampIcon={<ReleasesIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  const suitesColumn: GridColDef<FeatureDto> = {
    field: 'suiteLatestRunPassed',
    headerName: 'Latest Suites',
    flex: columnFlex.suites,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = {
        passed: params.row.suiteLatestRunPassed ?? 0,
        failed: params.row.suiteLatestRunFailed ?? 0,
        errored: params.row.suiteLatestRunErrored ?? 0,
        skipped: params.row.suiteLatestRunSkipped ?? 0,
        untested: params.row.suiteLatestRunUntested ?? 0,
      };
      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell entity="suite" counts={counts} />
        </TableCellContainer>
      );
    },
  };

  const testsColumn: GridColDef<FeatureDto> = {
    field: 'testLatestRunPassed',
    headerName: 'Latest Tests',
    flex: columnFlex.tests,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = {
        passed: params.row.testLatestRunPassed ?? 0,
        failed: params.row.testLatestRunFailed ?? 0,
        errored: params.row.testLatestRunErrored ?? 0,
        skipped: params.row.testLatestRunSkipped ?? 0,
        untested: params.row.testLatestRunUntested ?? 0,
      };
      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell entity="test" counts={counts} />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<FeatureDto> = {
    field: 'actions',
    headerName: '',
    flex: 0.12,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <TableActionsCell
        rowName={params.row.name}
        editActionLabel={getEntityActionLabel('edit', 'Feature')}
        editTagsEntityNoun="feature"
        rowTags={params.row.tags ?? []}
        rowTagColors={(params.row.tags ?? []).map((tag) => resolveTagColor(tag, tagColorByName))}
        onUpdateTagsColor={({ tags, colors }) => {
          onUpdateRowTagsColor?.({ row: params.row, tags, colors });
        }}
        updateTagsColorLoading={updateRowTagsColorPendingId === params.row.id}
        deleteActionLabel={getEntityActionLabel('delete', 'Feature')}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  return [nameColumn, statusColumn, suitesColumn, testsColumn, actionsColumn];
};
